import React, { useState } from "react";
import { FormattedBody } from "./formatted-body";

/** Collapsed post body shows this many characters before "Read more". */
export const EXPANDABLE_BODY_PREVIEW_LENGTH = 500;

/**
 * Truncate body text at `limit` without splitting an in-progress mention token.
 * @param {string} text
 * @param {number} limit
 * @returns {string}
 */
export function truncateFormattedBodyText(text, limit) {
  if (typeof text !== "string" || text.length <= limit) {
    return text || "";
  }
  let cut = text.slice(0, limit);
  const danglingMention = cut.match(/@\[[^\]]*$/);
  if (danglingMention && danglingMention.index != null) {
    cut = cut.slice(0, danglingMention.index);
  }
  return cut;
}

/**
 * Formatted body that collapses long text to a character limit with Read more / Show less.
 * @param {Object} props
 * @param {React.ElementType} [props.as]
 * @param {string} [props.className]
 * @param {string} [props.linkClassName]
 * @param {string} [props.mentionClassName]
 * @param {Array<object>} [props.mentions]
 * @param {number} [props.limit]
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element | null}
 */
function ExpandableFormattedBody({
  as = "p",
  className,
  linkClassName,
  mentionClassName,
  mentions,
  limit = EXPANDABLE_BODY_PREVIEW_LENGTH,
  children,
  ...props
}) {
  const [expanded, setExpanded] = useState(false);
  const text =
    children == null || children === ""
      ? ""
      : typeof children === "string"
        ? children
        : String(children);

  if (!text) {
    return null;
  }

  const isLong = text.length > limit;
  const displayText =
    expanded || !isLong
      ? text
      : `${truncateFormattedBodyText(text, limit)}...`;

  return (
    <div>
      <FormattedBody
        as={as}
        className={className}
        linkClassName={linkClassName}
        mentionClassName={mentionClassName}
        mentions={mentions}
        {...props}
      >
        {displayText}
      </FormattedBody>
      {isLong ? (
        <button
          type="button"
          className="mt-1 text-compact font-emphasis text-accent hover:text-accent-hover hover:underline"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      ) : null}
    </div>
  );
}

export { ExpandableFormattedBody };
